import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useSessionStore } from '@/store/sessionStore';
import { useCan } from '@/auth/consoleCapabilityContext';
import { fetchMeAccess, MeAccessUnauthorizedError, type MeAccessTenant } from '@/api/meAccess';
import { Button } from '@/components/ui';
import { AppCard } from './AppCard';
import { HomeKpiStrip } from './HomeKpiStrip';
import { HomeActionQueue } from './HomeActionQueue';
import { HomeAppCoverage } from './HomeAppCoverage';
import { HomeAllocationChart } from './HomeAllocationChart';
import { HomeAppsPending } from './HomeAppsPending';
import { HomeRecentActivity } from './HomeRecentActivity';
import { DASHBOARD_GATE_CAPABILITIES, useDashboardQuery } from './useDashboard';
import home from './Home.module.css';
import styles from './LauncherPage.module.css';

/**
 * `/` — the portal home screen (F4-lite, D14). MVP surface per
 * design-frontend.md: apps this user can enter, driven by `GET /me/access` —
 * never token claims.
 *
 * EAA-1756: wires the stub up to the real auth-client + portal-api
 * `/me/access` call. Renders the caller's identity, their tenants (as a
 * picker that writes `activeTenantId` into the session store — which then
 * flows back as the `X-Tenant-Id` header on the next `/me/access` fetch), and
 * the registry app cards.
 *
 * EAA-1701 (A5.3): app cards moved to `AppCard` — each its own fail-soft query
 * (the Content Studio card shows a live program count from the RS256 token);
 * cards render only when the user has a membership (empty ≠ error).
 *
 * EAA-1814 (S3 rebuild — the IA change, canonical frame 09 "Inset shell
 * home"): `/` moves INSIDE the `/console` shell (`App.tsx` now mounts this
 * route as a child of `ConsoleLayout`, not a sibling) — "Bảng điều khiển" is
 * the shell's first sidebar item, not a separate launcher-then-console
 * split. Three consequences of that move, all visible below:
 *
 *   1. No `<AppBar/>` here any more — `ConsoleLayout` already renders the
 *      shell's own topbar (`ConsoleTopbar`) around this page's `<Outlet/>`.
 *   2. `useCan()` reads the REAL `ConsoleCapabilityProvider` context
 *      `ConsoleLayout` mounts (it fetches `GET /me/admin` once, upstream of
 *      this page) instead of the old local `resolveCapabilities(...)`
 *      workaround this page needed while it sat outside the shell.
 *   3. The old "Quản trị" doorway card (a `Link` to `/console`) is gone —
 *      there is nothing left to "enter"; every console screen is already one
 *      sidebar click away.
 *
 * The admin dashboard regions (KPI row, "Cần bạn xử lý", coverage/allocation
 * cards, "Nhật ký gần đây") now read the single `GET /dashboard` aggregate
 * (EAA-1829) via `useDashboardQuery` — see `home/useDashboard.ts`. There is
 * still no `console.home.*` capability: each region gates on the capability
 * of the data it shows, and a caller with NONE of `DASHBOARD_GATE_CAPABILITIES`
 * (a plain user) sees no admin region at all, only the greeting + the
 * existing tenant/app-launcher grid below — which is UNCHANGED by this pass
 * (preserved, not deleted, per the ticket).
 *
 * DEPENDENCY ON `ConsoleLayout.tsx` (owned by a parallel EAA-1814 agent, not
 * this file): frame 11 ("Plain user") shows NO console nav/sidebar at all for
 * a capability-less caller. `ConsoleLayout`'s own nav-group filtering already
 * empties every group for such a caller, but the sidebar shell itself (the
 * acting-org card + "Về trang chính" link) still renders — this page cannot
 * fix that from inside `<Outlet/>`. See this ticket's report for the
 * coordination note.
 */
export function LauncherPage() {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { authenticated, accessToken, claims, reauthenticate } = useAuth();
    const activeTenantId = useSessionStore((state) => state.activeTenantId);
    const setActiveTenantId = useSessionStore((state) => state.setActiveTenantId);
    const apps = Object.entries(config.apps);
    const can = useCan();

    const meAccessQuery = useQuery({
        queryKey: ['me-access', accessToken, activeTenantId],
        queryFn: () => {
            // accessToken is guaranteed non-null here by `enabled` below.
            return fetchMeAccess(config.apiBase, accessToken as string, activeTenantId);
        },
        enabled: authenticated && Boolean(accessToken),
        retry: false,
    });

    // EAA-1814: is this caller a portal admin of ANY kind? Drives whether the
    // dashboard regions attempt `GET /dashboard` at all (a plain user gets a
    // guaranteed 403 — IsPortalAdmin — so the fetch is skipped rather than
    // fired-and-erroring) and whether the "Bảng điều khiển" header shows.
    const hasConsoleAccess = DASHBOARD_GATE_CAPABILITIES.some(can);
    const baseEnabled = authenticated && Boolean(accessToken);
    // Shares its cache entry with every Home* region below (same query key,
    // see useDashboard.ts) — mounting all of them here costs one request.
    const dashboardQuery = useDashboardQuery(
        config.apiBase,
        accessToken,
        baseEnabled && hasConsoleAccess
    );
    const canAccounts = can('console.accounts.issue');

    useEffect(() => {
        // Fail-soft per the auth guard contract: a 401 from /me/access means
        // the session portal-api sees is no longer valid — re-authenticate
        // rather than surface it as a page error.
        // EAA-1825: BOUNDED now. A 401 does not always mean "the session ended" —
        // when portal-api rejects a bearer Keycloak will happily keep minting
        // (audience/scope/key mismatch), the old `client.login('/')` reloaded the
        // whole tab, got another 401, and looped forever. `reauthenticate` stops
        // after the round trip proves it is not converging.
        if (meAccessQuery.error instanceof MeAccessUnauthorizedError) {
            reauthenticate('/');
        }
    }, [meAccessQuery.error, reauthenticate]);

    const tenants: MeAccessTenant[] = meAccessQuery.data?.tenants ?? [];
    const showSoftError =
        meAccessQuery.isError && !(meAccessQuery.error instanceof MeAccessUnauthorizedError);
    const greetingName = claims?.name ?? claims?.email ?? claims?.sub ?? '';
    const noAccess = meAccessQuery.isSuccess && tenants.length === 0;

    return (
        <div className={styles.page}>
            {hasConsoleAccess ? (
                <>
                    {/* EAA-1814: frame 09 lines 402-436 — "Bảng điều khiển" title +
                        primary "Cấp tài khoản" shortcut. NO date-range control:
                        `GET /dashboard` (dashboard_views.py) takes no query params at
                        all — shipping the frame's date chip would be a control that
                        changes nothing, which the ticket says not to build. */}
                    <div className={styles.header}>
                        <h1 className={styles.headerTitle}>{t('home.launcher.dashboardTitle')}</h1>
                        {/* EAA-1804: kit `Button` instead of hand-written `className="portal-btn"` on the
                            react-router `Link` — `Link` stays outer so this is still client-side nav,
                            same pattern as `ConfigPage.tsx`'s audit-log CTA. */}
                        {canAccounts && (
                            <Link to="/console/accounts">
                                <Button type="primary">
                                    {t('home.launcher.issueAccountsCta')}
                                </Button>
                            </Link>
                        )}
                    </div>

                    <HomeKpiStrip
                        can={can}
                        apiBase={config.apiBase}
                        accessToken={accessToken}
                        authenticated={authenticated}
                    />

                    <div className={styles.midGrid}>
                        <HomeActionQueue
                            can={can}
                            apiBase={config.apiBase}
                            accessToken={accessToken}
                            authenticated={authenticated}
                        />
                        <HomeAppCoverage can={can} />
                        <HomeAllocationChart
                            can={can}
                            provisioning={dashboardQuery.data?.kpis.provisioning}
                        />
                    </div>

                    <HomeRecentActivity
                        can={can}
                        apiBase={config.apiBase}
                        accessToken={accessToken}
                        authenticated={authenticated}
                    />
                </>
            ) : (
                // EAA-1814: frame 11 "Plain user" — no dashboard region at all
                // (fail-closed: no console.* capability, so no attempt to call
                // IsPortalAdmin-gated /dashboard), just the greeting. The
                // tenant/app sections below are UNCHANGED and still render for
                // this caller too — vai trò comes from the school, but once
                // granted, apps appear here without a re-login (existing
                // meAccess polling behaviour, untouched by this pass).
                <div className={styles.plainHero}>
                    {authenticated && greetingName && (
                        <h1 className={styles.plainHeroTitle}>
                            {t('home.launcher.greeting', { name: greetingName })}
                        </h1>
                    )}
                    <p className={styles.plainHeroText}>{t('home.launcher.loggedInMessage')}</p>
                </div>
            )}

            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>{t('home.launcher.organizationsTitle')}</h2>

                {meAccessQuery.isLoading && (
                    <div className={styles.skeleton} aria-busy="true">
                        {t('home.launcher.loadingAccess')}
                    </div>
                )}

                {showSoftError && (
                    <p className="portal-launcher-soft-error">
                        {t('home.launcher.accessLoadError')}
                    </p>
                )}

                {noAccess && (
                    <div className="portal-empty">
                        <BuildingIcon />
                        <span className="portal-empty-title">{t('home.launcher.noOrgTitle')}</span>
                        <span className="portal-empty-hint">{t('home.launcher.noOrgHint')}</span>
                    </div>
                )}

                {tenants.length > 0 && (
                    <div className={styles.grid}>
                        {tenants.map((tenant) => (
                            <button
                                key={tenant.tenant_id}
                                type="button"
                                className={
                                    home.launcherCard +
                                    (tenant.tenant_id === activeTenantId
                                        ? ' ' + home.tenantCardActive
                                        : '')
                                }
                                onClick={() => setActiveTenantId(tenant.tenant_id)}
                            >
                                <span className="portal-avatar" aria-hidden="true">
                                    {(tenant.name ?? '?').trim().charAt(0)}
                                </span>
                                <strong className={home.launcherCardTitle}>{tenant.name}</strong>
                                <span className="portal-muted">{tenant.slug}</span>
                                {tenant.roles.length > 0 && (
                                    <small className={home.tenantCardRoles}>
                                        {t('home.launcher.rolesLabel', {
                                            roles: tenant.roles.join(', '),
                                        })}
                                    </small>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </section>

            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>{t('home.launcher.appsTitle')}</h2>

                {/* EAA-1701 (A5.3): apps render only for a user WITH a usable
                    membership (registry ∩ /me/access). Empty ≠ error: a verified
                    token with no membership shows guidance, never a failure banner. */}
                {noAccess ? (
                    <div className="portal-empty">
                        <GridIcon />
                        <span className="portal-empty-title">{t('home.launcher.noAppsTitle')}</span>
                        <span className="portal-empty-hint">
                            {/* EAA-1814: copy restored verbatim from frame 11 (line
                                ~106) — was previously paraphrased. */}
                            {t('home.launcher.noAppsHint')}
                        </span>
                    </div>
                ) : (
                    <div className={styles.grid}>
                        {apps.map(([code, app]) => (
                            <AppCard key={code} code={code} app={app} />
                        ))}
                        {/* EAA-1814: additive only — renders nothing unless the
                            caller has console.apps.view AND the registry has apps
                            awaiting approval. Never removes/hides an AppCard above. */}
                        <HomeAppsPending
                            can={can}
                            apiBase={config.apiBase}
                            accessToken={accessToken}
                            authenticated={authenticated}
                        />
                    </div>
                )}
            </section>
        </div>
    );
}

/* Inline icons — no icon dependency (design-pass rule). Decorative (aria-hidden
   handled by the wrapper). */
function BuildingIcon() {
    return (
        <svg
            className="portal-empty-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
        >
            <path
                d="M3 21h18M6 21V5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v16M14 21V9h3a1 1 0 0 1 1 1v11M9 8h1m-1 4h1m-1 4h1"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function GridIcon() {
    return (
        <svg
            className="portal-empty-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
        >
            <rect x="3" y="3" width="7" height="7" rx="1.5" />
            <rect x="14" y="3" width="7" height="7" rx="1.5" />
            <rect x="3" y="14" width="7" height="7" rx="1.5" />
            <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
    );
}
